import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import SecretsManager from "@/components/SecretsManager";
import { Plug, CreditCard, KeyRound, Plus, Trash2, CheckCircle2, AlertTriangle, ExternalLink, Sparkles, History, Download } from "lucide-react";

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div className="k-container" data-testid={testid} style={{ padding: 20, marginBottom: 16 }}>
      <h3 className="k-heading" style={{ fontSize: 16, marginTop: 0, display: "flex", alignItems: "center", gap: 8 }}>
        {Icon && <Icon size={16} aria-hidden />} {title}
      </h3>
      {children}
    </div>
  );
}

export default function ManageTab({ projectId }) {
  const confirm = useConfirm();
  const [conn, setConn] = useState(null);
  const [pay, setPay] = useState(null);
  const [depId, setDepId] = useState(null);
  const [open, setOpen] = useState({});
  const [filters, setFilters] = useState({ q: "", sector: "", access_class: "" });
  const [activity, setActivity] = useState([]);

  const loadConn = useCallback(() => {
    api.get(`/builder/${projectId}/connectors`, { params: filters }).then((r) => setConn(r.data)).catch(() => {});
  }, [projectId, filters]);
  useEffect(loadConn, [loadConn]);
  useEffect(() => {
    api.get(`/builder/${projectId}/payments`).then((r) => setPay(r.data)).catch(() => {});
    api.get(`/hosting/${projectId}/status`).then((r) => setDepId((r.data.deployments || [])[0]?.id || null)).catch(() => {});
    api.get(`/builder/${projectId}/activity`).then((r) => setActivity(r.data.entries || [])).catch(() => {});
  }, [projectId]);

  const addConn = async (id) => { try { await api.post(`/projects/${projectId}/add-catalogue`, { entry_id: id }); toast.success("Added — routed through verification"); loadConn(); } catch (e) { toast.error(errMsg(e)); } };
  const removeConn = async (c) => {
    let msg = "Remove this connector from the project?";
    try { const { data } = await api.get(`/builder/${projectId}/connectors/${c.id}/impact`); msg = data.warning; } catch { /* fall back */ }
    const ok = await confirm({ title: `Remove ${c.name}?`, body: msg, confirmLabel: "Remove" });
    if (!ok) return;
    try { await api.delete(`/builder/${projectId}/connectors/${c.id}`); loadConn(); toast.success("Removed"); } catch (e) { toast.error(errMsg(e)); }
  };
  const addBundle = async (sector) => { try { const { data } = await api.post(`/builder/${projectId}/connectors/bundle`, { sector }); toast.success(`Bundle added (${data.added})`); loadConn(); } catch (e) { toast.error(errMsg(e)); } };
  const setProvider = async (provider) => { try { await api.post(`/builder/${projectId}/payment-provider`, { provider }); api.get(`/builder/${projectId}/payments`).then((r) => setPay(r.data)); toast.success(`Provider: ${provider}`); } catch (e) { toast.error(errMsg(e)); } };
  const setMode = async (mode) => { try { await api.post(`/builder/${projectId}/payments/mode`, { mode }); setPay((p) => ({ ...p, mode })); } catch (e) { toast.error(errMsg(e)); } };
  const health = async () => { try { const { data } = await api.post(`/builder/${projectId}/payments/health-check`); setPay((p) => ({ ...p, connection_health: data.health })); toast.success(`Health: ${data.health}`); } catch (e) { toast.error(errMsg(e)); } };
  const testCharge = async () => { try { const { data } = await api.post(`/builder/${projectId}/payments/test-charge`); toast.success(`Test charge ${data.amount}: ${data.result}`); } catch (e) { toast.error(errMsg(e)); } };
  // G3 (Patch 65) — real, working download of the Manage-tab activity log (from the existing Menu 10
  // audit trail). CSV built client-side from the already-loaded entries; no new data source.
  const downloadActivity = () => {
    const rows = activity || [];
    if (!rows.length) return;
    const cols = Array.from(rows.reduce((s, r) => { Object.keys(r).forEach((k) => s.add(k)); return s; }, new Set()));
    const esc = (v) => `"${String(v == null ? "" : typeof v === "object" ? JSON.stringify(v) : v).replace(/"/g, '""')}"`;
    const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `kadana-activity-${projectId}.csv`; a.click();
  };

  return (
    <div data-testid="manage-tab">
      <Card title="Connectors — grouped by region (appendix-k)" icon={Plug} testid="manage-connectors">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
          <input className="k-control" data-testid="conn-search" placeholder="Search connectors…" style={{ maxWidth: 220 }} value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
          <select className="k-control" data-testid="conn-filter-sector" style={{ width: "auto" }} value={filters.sector} onChange={(e) => setFilters((f) => ({ ...f, sector: e.target.value }))}>
            <option value="">All sectors</option>
            {(conn?.sectors || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="k-control" data-testid="conn-filter-access" style={{ width: "auto" }} value={filters.access_class} onChange={(e) => setFilters((f) => ({ ...f, access_class: e.target.value }))}>
            <option value="">All access classes</option>
            {(conn?.access_classes || []).map((a) => <option key={a} value={a}>Class {a}</option>)}
          </select>
          {filters.sector && <button className="k-btn k-btn-ghost" data-testid="conn-bundle" onClick={() => addBundle(filters.sector)}>+ Add all {filters.sector}</button>}
        </div>
        {(conn?.suggested || []).length > 0 && (
          <div data-testid="conn-suggested" style={{ marginBottom: 10 }}>
            <span className="k-muted" style={{ fontSize: 12 }}><Sparkles size={12} /> Suggested for your app: </span>
            {conn.suggested.map((s) => (
              <button key={s.id} className="k-badge" data-testid={`suggest-${s.id}`} style={{ cursor: "pointer", margin: 2 }} onClick={() => addConn(s.id)} title={s.why}>+ {s.name}</button>
            ))}
          </div>
        )}
        {(conn?.groups || []).map((g) => (
          <div key={g.bloc} data-testid={`conn-bloc-${g.bloc}`} style={{ marginBottom: 8 }}>
            <button className="k-badge" style={{ cursor: "pointer", padding: "6px 10px" }} onClick={() => setOpen((o) => ({ ...o, [g.bloc]: !o[g.bloc] }))}>{open[g.bloc] ? "▾" : "▸"} {g.name}</button>
            {open[g.bloc] && (
              <div style={{ padding: "8px 0 8px 14px" }}>
                {g.members.map((m) => (
                  <div key={m.code} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{m.name}</div>
                    {m.empty_note ? <div className="k-muted" style={{ fontSize: 12 }}>{m.empty_note}</div> :
                      m.connectors.map((c) => (
                        <div key={c.id} data-testid={`conn-${c.id}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "2px 0" }}>
                          <span style={{ flex: 1 }}>{c.name} <span className="k-badge" style={{ fontSize: 10 }}>{c.sector}</span>
                            {c.health === "operational" ? <CheckCircle2 size={12} className="k-tag-green" aria-label="operational" /> : <AlertTriangle size={12} className="k-tag-warn" aria-label="unverified" />}
                            {c.doc_url && <a href={c.doc_url} target="_blank" rel="noreferrer" data-testid={`conn-doc-${c.id}`} title="Provider developer docs" style={{ marginLeft: 4 }}><ExternalLink size={11} /></a>}
                          </span>
                          {c.added
                            ? <button className="k-btn k-btn-ghost" data-testid={`conn-remove-${c.id}`} onClick={() => removeConn(c)} style={{ padding: "3px 8px" }}><Trash2 size={12} /></button>
                            : <button className="k-btn k-btn-ghost" data-testid={`conn-add-${c.id}`} onClick={() => addConn(c.id)} style={{ padding: "3px 8px" }}><Plus size={12} /></button>}
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </Card>

      <Card title="Payments — your app takes payments" icon={CreditCard} testid="manage-payments">
        <div className="k-container" style={{ padding: 12, marginBottom: 10, borderLeft: "3px solid var(--accent-primary)" }}>
          <p className="k-muted" style={{ fontSize: 13, margin: 0 }}><strong>Connects a payment provider so the app you're building can take money from its own end users.</strong> This is <em>not</em> how you pay Kadana for credits — for that, use the <strong>Credits</strong> tab.</p>
        </div>
        <div style={{ overflowX: "auto", marginBottom: 10 }}>
          <table data-testid="pay-comparison" style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead><tr style={{ textAlign: "left" }}><th style={{ padding: 4 }}>Provider</th><th>Fees (their published rates)</th><th>Payout</th><th>Features</th><th></th></tr></thead>
            <tbody>
              {(pay?.comparison || []).map((p) => (
                <tr key={p.provider} style={{ borderTop: "1px solid var(--control)" }} data-testid={`pay-row-${p.provider}`}>
                  <td style={{ padding: 4, fontWeight: 600 }}>{p.provider}{pay?.provider === p.provider && <CheckCircle2 size={12} className="k-tag-green" style={{ marginLeft: 4 }} />}</td>
                  <td>{p.fee}</td><td>{p.payout}</td>
                  <td className="k-muted">{(p.features || []).join(" · ")}</td>
                  <td><button className="k-btn k-btn-ghost" data-testid={`pay-connect-${p.provider}`} onClick={() => setProvider(p.provider)} style={{ padding: "3px 8px" }}>Connect</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pay?.provider && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", border: "1px solid var(--control)", borderRadius: 8, overflow: "hidden" }}>
              {["sandbox", "live"].map((m) => <button key={m} data-testid={`pay-mode-${m}`} className={`k-btn ${pay.mode === m ? "k-btn-primary" : "k-btn-ghost"}`} onClick={() => setMode(m)} style={{ padding: "4px 10px", fontSize: 12 }}>{m}</button>)}
            </div>
            <button className="k-btn k-btn-ghost" data-testid="pay-health" onClick={health}>Health check</button>
            <span className="k-badge" data-testid="pay-health-status">{pay.connection_health}</span>
            <button className="k-btn k-btn-ghost" data-testid="pay-test-charge" onClick={testCharge} disabled={pay.mode !== "sandbox"}>Run sandbox test charge</button>
            <span className="k-muted" style={{ fontSize: 12 }}>Fees: {pay.fee_summary}</span>
            {pay.webhook && <span className="k-muted" style={{ fontSize: 12 }} data-testid="pay-webhook">Webhook: <span className="mono">{pay.webhook.url}</span> ({pay.webhook.events.join(", ")})</span>}
          </div>
        )}
      </Card>

      <Card title="Secrets" icon={KeyRound} testid="manage-secrets">
        <SecretsManager deploymentId={depId} compact />
      </Card>

      <Card title="Activity — connectors, payments & secrets (Menu 10)" icon={History} testid="manage-activity">
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <button className="k-btn k-btn-ghost" data-testid="activity-download" onClick={downloadActivity} disabled={!activity.length} style={{ padding: "4px 10px", fontSize: 12 }}><Download size={13} /> Download CSV</button>
        </div>
        {activity.length === 0 ? <p className="k-muted" style={{ fontSize: 13 }}>No Manage-tab changes logged yet.</p> :
          activity.map((e) => (
            <div key={e.id || e.seq} style={{ fontSize: 12, padding: "3px 0", borderTop: "1px solid var(--control)" }}>
              <span className="mono">{String(e.at).slice(0, 19).replace("T", " ")}</span> · <strong>{e.action}</strong> {e.details?.name || e.details?.provider || e.details?.entry_id || ""}
            </div>
          ))}
        <p className="k-muted" style={{ fontSize: 11, marginTop: 8 }}>One consolidated view pulled from the existing hash-chained Menu 10 audit trail — not a second log.</p>
      </Card>
    </div>
  );
}
